<template>
  <div>
    <base-widget
      ref="widget"
      expanded
      :initial-tab="1"
      :items="tabs"
      :style="minHeight"
      @scroll="onScroll"
      @tabChanged="$emit('tab-changed', $event)"
    >
      <div class="d-flex align-items-center mb-2 justify-content-between">
        <base-breadcrumb
          :routes="breadcrumb"
          class="my-0"
        />
      </div>
      <div class="d-flex align-items-center mb-4 justify-content-between">
        <!--Group Name -->
        <div
          class="d-flex align-items-center heading-container"
          :class="{ 'non-editable-name-container': !editMode }"
          @click="startEditingName"
        >
          <base-text-editable
            v-if="editMode"
            ref="editableName"
            v-model="newname"
            max-width="600"
            @complete="changeName()"
            @blur="changeName()"
          />
          <p
            v-else
            class="group-headline non-editable-name"
          >
            {{ groupName }}
          </p>
          <base-text
            v-if="count > 0"
            size="m"
          >
            &nbsp;({{ count }})
          </base-text>
        </div>
        <div class="d-flex">
          <base-search
            class="search-bar"
            icon-position="left"
            :value="search"
            @input="$emit('on-search', $event)"
          />

          <base-button
            v-if="!(groupInfo.is_supergroup || groupInfo.via_integration)"
            class="custombutton-clients"
            @click="$emit('add-clients')"
          >
            <base-icon
              icon="mplus-client"
            />Add Client
          </base-button>
        </div>
      </div>
      <base-list
        :loading="loading"
        :items="items"
        mode="loadMore"
        show-load-more
        :page-size="pageSize"
        :count="count"
        :full-height="true"
        @pageChange="$emit('page-change', $event)"
      >
        <template slot="loading">
          <base-skeleton
            v-for="n in 4"
            :key="n"
            :opacity="0 + (1 /n)"
          >
            <div class="d-flex align-items-center">
              <div class="skeleton__circle mr-4" />
              <div
                class="skeleton__item mr-5"
                style="max-width: 150px"
              />
              <div
                class="skeleton__item mt-0"
                style="max-width: 100px"
              />
            </div>
          </base-skeleton>
        </template>
        <template v-slot:default="{item}">
          <GroupDetailCell
            :item="item"
            @click="$emit('cell-clicked', item)"
            @delete="$emit('delete', item)"
          />
        </template>
        <template slot="nodata">
          <template v-if="search !== ''">
            <empty title="No clients matched your search." />
          </template>
          <template v-else>
            <empty title="No clients in this group yet">
              <span v-if="!(groupInfo.is_supergroup || groupInfo.via_integration)">
                <a

                  href="#"
                  class="link-orange font-weight-bold"
                  @click.prevent="$emit('add-clients')"
                > Add a client </a>to your group
              </span>
            </empty>
          </template>
        </template>
      </base-list>
    </base-widget>
  </div>
</template>
<script>

import _ from 'lodash';
import BaseTextEditable from '@/components/Base/BaseTexteditable';
import hoverable from '@/components/mixins/hoverable';
import GroupDetailCell from './GroupsList/GroupDetailCell';
import minHeightMixin from '../mixins/minheight';

export default {
  name: 'GroupDetail',
  components: {
    GroupDetailCell,
    BaseTextEditable,
  },
  mixins: [minHeightMixin, hoverable],
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    breadcrumb: {
      type: Array,
      default: () => [],
    },
    search: {
      type: String,
      default: '',
    },
    count: {
      type: Number,
      default: 0,
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    groupName: {
      type: String,
      required: true,
    },
    groupInfo: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      editMode: false,
      newname: '',
    };
  },
  computed: {
    isAdminMode() {
      return this.$store.state.account.isAdminMode;
    },
    permissionViewsAllClient() {
      return this.usePermission('view_all_clients') || this.useFeature('edits_sendable_to_all_institution_clients');
    },
    tabs() {
      if (this.isAdminMode) {
        return ['Clients', 'Groups'];
      }
      if (this.permissionViewsAllClient) {
        return ['My Clients', 'All Clients', 'Groups'];
      }
      return ['My Clients', 'Groups'];
    },
  },
  watch: {
    groupName(newval) {
      this.newname = newval;
    },
  },
  mounted() {
    window.onscroll = _.debounce((ev) => this.onScroll(ev), 200);
  },
  methods: {
    changeName() {
      if (this.newname) this.$emit('group-name-change', this.newname);
      else this.newname = this.groupName;
      this.editMode = false;
    },
    startEditingName() {
      this.editMode = true;
      this.$nextTick(() => this.$refs.editableName.focus());
    },
    onScroll({ target: { scrollingElement: { scrollTop, clientHeight, scrollHeight } } }) {
      if (scrollTop + clientHeight >= scrollHeight) {
        // Bottom reached
        if (this.items.length <= this.count) {
          // Still elements to retrieve
          this.$emit('reached-bottom');
        }
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.search-bar {
  margin-left: auto;
  min-width: 310px;

  &::v-deep input {
    // min-height: 42px;
    font-size: 0.875rem;
  }
}
.create-button {
  min-width: 95px;
  margin-left: 1rem;
}
.group-headline {
  font-family: Tiempos;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  letter-spacing: 0.544px;
}
.edit-icon {
  opacity: 0;
  &--hover {
    opacity: 1;
  }
}
::v-deep .sl-card--expanded {
  padding-top: 36px !important;
}

::v-deep .base-texteditable {
  border: 2px solid #D1B0A0 !important;
  height: 34px;
  padding-left: 7px;
  border-radius: 5px;
  font-family: $font-family-serif;
  font-style: normal;
  font-weight: 300 !important;
  font-size: 20px;
  line-height: 24px;
}

::v-deep .base-heading {
  cursor: default;
}

.non-editable-name-container {
  white-space: nowrap;
  overflow: hidden;
  width: 100%;

  & .non-editable-name {
    width: auto;
    max-width: calc(100% - 30px);
  }

  & p {
    font-family: $font-family-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
    overflow: hidden;
    text-overflow: ellipsis !important;
    margin: 0;
  }
}

.heading-container {
  width: 100%;
  overflow: hidden;
}
::v-deep .base-search {
    margin-right: 0;
    max-width: 320px;
    &__input {
      // font
      font-family: 'SilkaLight' !important;
      // design
      background-color: #FFF;
      border: 1px solid #C6C6C6;
      border-radius: 5px;
      font-family: $font-family-sans-serif-medium;
      max-width: 100%;
    }
  }
::v-deep .base-search__icon--left {
  margin-left: 5px;
}
.custombutton-clients{
  font-weight: 500;
  font-size: 14px;
  color: #FFF;
  display: flex;
  padding: 8.5px 12px;
  gap: 12px;
  margin-left: 16px;
  margin-right: unset;
  max-width: 140px;
  min-width: 130px;
  max-height: 36px;
  background: #333333;
  border-radius: 5px;
}
::v-deep .base-tabs__item{
  margin-left: 4px;
  color: #AAAAAA;
  &.active {
    color: #333333;
  }
}
::v-deep .base-cell{
  min-height: 72px !important;
}
</style>
